import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  Divider,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import axios from 'axios';
import { API_BASE_URL } from '../services/api';

enum ReportType {
  POST = 'POST',
  COMMENT = 'COMMENT',
  USER = 'USER',
}

enum ReportStatus {
  PENDING = 'PENDING',
  REVIEWING = 'REVIEWING',
  RESOLVED = 'RESOLVED',
  REJECTED = 'REJECTED',
}

enum ReportReason {
  SPAM = 'SPAM',
  INAPPROPRIATE = 'INAPPROPRIATE',
  HARASSMENT = 'HARASSMENT',
  ILLEGAL = 'ILLEGAL',
  COPYRIGHT = 'COPYRIGHT',
  FALSE_INFO = 'FALSE_INFO',
  ETC = 'ETC',
}

const reportTypeLabels = {
  POST: '게시물',
  COMMENT: '댓글',
  USER: '사용자',
};

const reportStatusLabels = {
  PENDING: '대기중',
  REVIEWING: '검토중',
  RESOLVED: '처리완료',
  REJECTED: '기각됨',
};

const reportReasonLabels = {
  SPAM: '스팸/광고',
  INAPPROPRIATE: '부적절한 내용',
  HARASSMENT: '괴롭힘/욕설',
  ILLEGAL: '불법 정보',
  COPYRIGHT: '저작권 침해',
  FALSE_INFO: '허위 정보',
  ETC: '기타',
};

interface Report {
  id: number;
  type: ReportType;
  status: ReportStatus;
  reason: ReportReason;
  description: string | null;
  reviewNote: string | null;
  reviewedAt: string | null;
  createdAt: string;
  updatedAt: string;
  reporter: {
    id: number;
    username: string;
    name: string;
    email: string;
    profilePic: string | null;
    createdAt: string;
  };
  post?: {
    id: number;
    description: string;
    visibility?: 'PUBLIC' | 'PRIVATE' | 'HIDDEN';
    hiddenAt?: string | null;
    hiddenReason?: string | null;
    author: {
      id: number;
      username: string;
      name: string;
      profilePic: string | null;
    };
    images: Array<{ id: number; basename: string }>;
    _count: {
      likes: number;
      comments: number;
      reports: number;
    };
  };
  comment?: {
    id: number;
    description: string;
    commentBy: {
      id: number;
      username: string;
      name: string;
      profilePic: string | null;
    };
    post: {
      id: number;
      description: string;
    };
    _count: {
      reports: number;
    };
  };
  reviewer?: {
    id: number;
    username: string;
    name: string;
    email: string;
  };
}

const ReportDetail: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [report, setReport] = useState<Report | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const [status, setStatus] = useState<ReportStatus>(ReportStatus.PENDING);
  const [reviewNote, setReviewNote] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const [postActionOpen, setPostActionOpen] = useState<null | 'hide' | 'delete' | 'unhide'>(null);
  const [postActionReason, setPostActionReason] = useState('');
  const [isPostActionBusy, setIsPostActionBusy] = useState(false);

  useEffect(() => {
    fetchReport();
  }, [id]);

  const fetchReport = async () => {
    try {
      setIsLoading(true);
      setError('');

      const token = localStorage.getItem('accessToken');
      const response = await axios.get(`${API_BASE_URL}/admin/reports/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setReport(response.data);
      setStatus(response.data.status);
      setReviewNote(response.data.reviewNote || '');
    } catch (err: any) {
      setError(err.response?.data?.message || '신고를 불러오는데 실패했습니다.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleUpdateStatus = async () => {
    if (!id) return;

    try {
      setIsSaving(true);
      setError('');

      const token = localStorage.getItem('accessToken');
      await axios.post(
        `${API_BASE_URL}/admin/reports/${id}/status`,
        { status, reviewNote },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      fetchReport();
    } catch (err: any) {
      setError(err.response?.data?.message || '신고 상태 업데이트에 실패했습니다.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!id) return;

    try {
      const token = localStorage.getItem('accessToken');
      await axios.delete(`${API_BASE_URL}/admin/reports/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      navigate('/reports');
    } catch (err: any) {
      setError(err.response?.data?.message || '신고 삭제에 실패했습니다.');
    }
  };

  // 신고된 게시물 즉시 조치 (숨김 또는 삭제) + 신고 자동 RESOLVED.
  // - hide: 소프트 히든. 증거 보존, 필요 시 복구 가능. NSFW·부적절 사진 1차 대응 권장.
  // - delete: 완전 삭제. 되돌릴 수 없음. 명백한 스팸·불법 콘텐츠에만 사용.
  const handlePostAction = async () => {
    if (!id || !report?.post || !postActionOpen) return;
    setIsPostActionBusy(true);
    setError('');
    try {
      const token = localStorage.getItem('accessToken');
      const headers = { Authorization: `Bearer ${token}` };
      const postId = report.post.id;

      if (postActionOpen === 'hide') {
        await axios.post(
          `${API_BASE_URL}/admin/posts/${postId}/hide`,
          { reason: postActionReason || undefined, resolveReportIds: [Number(id)] },
          { headers },
        );
      } else if (postActionOpen === 'unhide') {
        await axios.post(
          `${API_BASE_URL}/admin/posts/${postId}/unhide`,
          {},
          { headers },
        );
      } else {
        await axios.delete(`${API_BASE_URL}/admin/posts/${postId}`, { headers });
        // 삭제 시 게시물 FK cascade 로 report 도 삭제되지만, 목록으로 이동해 상황 정리.
        navigate('/reports');
        return;
      }

      setPostActionOpen(null);
      setPostActionReason('');
      fetchReport();
    } catch (err: any) {
      setError(err.response?.data?.message || '조치 실패');
    } finally {
      setIsPostActionBusy(false);
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error && !report) {
    return <Alert severity="error">{error}</Alert>;
  }

  if (!report) {
    return <Alert severity="info">신고를 찾을 수 없습니다.</Alert>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/reports')}>
            목록으로
          </Button>
          <Typography variant="h4">신고 상세</Typography>
        </Box>
        <Button
          variant="contained"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={() => setDeleteDialogOpen(true)}
        >
          삭제
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', md: 'row' } }}>
        <Box sx={{ flex: { xs: 1, md: 2 } }}>
          {/* 신고 정보 */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              신고 정보
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">신고 타입</Typography>
              <Chip label={reportTypeLabels[report.type]} color="primary" size="small" sx={{ mt: 0.5 }} />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">신고 사유</Typography>
              <Typography variant="body1">{reportReasonLabels[report.reason]}</Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">신고 상세 내용</Typography>
              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mt: 1 }}>
                {report.description || '(상세 내용 없음)'}
              </Typography>
            </Box>

            <Typography variant="caption" color="text.secondary">
              신고일: {formatDate(report.createdAt)}
            </Typography>
          </Paper>

          {/* 신고된 콘텐츠 */}
          {report.post && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">신고된 게시물</Typography>
                {report.post.visibility === 'HIDDEN' && (
                  <Chip label="숨김 처리됨" color="warning" size="small" icon={<VisibilityOffIcon />} />
                )}
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Typography variant="body2" color="text.secondary" gutterBottom>
                작성자: {report.post.author.name} (@{report.post.author.username})
              </Typography>

              <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
                {report.post.description}
              </Typography>

              {/* 이미지 썸네일 그리드 — 관리자 즉시 판단용. */}
              {report.post.images && report.post.images.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    첨부 이미지 {report.post.images.length}장
                  </Typography>
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 1 }}>
                    {report.post.images.map((img) => (
                      <Box
                        key={img.id}
                        component="a"
                        href={`${API_BASE_URL}/uploads/posts/${img.basename}`}
                        target="_blank"
                        rel="noreferrer noopener"
                        sx={{
                          display: 'block',
                          aspectRatio: '1 / 1',
                          overflow: 'hidden',
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          bgcolor: 'grey.100',
                        }}
                      >
                        <Box
                          component="img"
                          src={`${API_BASE_URL}/uploads/posts/${img.basename}`}
                          alt={`신고 이미지 ${img.id}`}
                          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                        />
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}

              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Chip label={`좋아요 ${report.post._count.likes}`} size="small" variant="outlined" />
                <Chip label={`댓글 ${report.post._count.comments}`} size="small" variant="outlined" />
                <Chip label={`신고 ${report.post._count.reports}`} size="small" color="error" variant="outlined" />
              </Box>

              {/* 즉시 조치 — 신고 상세에서 원클릭으로 게시물 처리 + 신고 자동 RESOLVED */}
              <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => report.post && navigate(`/posts/${report.post.id}`)}
                >
                  게시물 상세 보기
                </Button>
                {report.post.visibility !== 'HIDDEN' ? (
                  <Button
                    variant="contained"
                    color="warning"
                    size="small"
                    startIcon={<VisibilityOffIcon />}
                    onClick={() => setPostActionOpen('hide')}
                  >
                    즉시 숨김
                  </Button>
                ) : (
                  <>
                    <Chip
                      icon={<VisibilityOffIcon />}
                      label={`숨김 처리됨${report.post.hiddenReason ? ` — ${report.post.hiddenReason}` : ''}`}
                      color="warning"
                      variant="outlined"
                    />
                    <Button
                      variant="contained"
                      color="success"
                      size="small"
                      startIcon={<VisibilityIcon />}
                      onClick={() => setPostActionOpen('unhide')}
                    >
                      숨김 해제
                    </Button>
                  </>
                )}
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  startIcon={<DeleteIcon />}
                  onClick={() => setPostActionOpen('delete')}
                >
                  게시물 즉시 삭제
                </Button>
              </Box>
            </Paper>
          )}

          {report.comment && (
            <Paper sx={{ p: 3, mb: 3 }}>
              <Typography variant="h6" gutterBottom>
                신고된 댓글
              </Typography>
              <Divider sx={{ mb: 2 }} />

              <Typography variant="body2" color="text.secondary" gutterBottom>
                작성자: {report.comment.commentBy.name} (@{report.comment.commentBy.username})
              </Typography>

              <Typography variant="body1" sx={{ mb: 2 }}>
                {report.comment.description}
              </Typography>

              <Typography variant="caption" color="text.secondary" display="block" gutterBottom>
                원본 게시물: {report.comment.post.description}
              </Typography>

              <Chip label={`신고 ${report.comment._count.reports}`} size="small" color="error" variant="outlined" />
            </Paper>
          )}

          {/* 상태 업데이트 */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              처리 정보
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel>신고 상태</InputLabel>
              <Select
                value={status}
                label="신고 상태"
                onChange={(e) => setStatus(e.target.value as ReportStatus)}
              >
                {Object.entries(reportStatusLabels).map(([key, label]) => (
                  <MenuItem key={key} value={key}>{label}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              multiline
              rows={4}
              label="처리 내역"
              value={reviewNote}
              onChange={(e) => setReviewNote(e.target.value)}
              sx={{ mb: 2 }}
            />

            <Button
              variant="contained"
              startIcon={<SaveIcon />}
              onClick={handleUpdateStatus}
              disabled={isSaving}
            >
              {isSaving ? '저장 중...' : '상태 업데이트'}
            </Button>

            {report.reviewedAt && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="caption" color="text.secondary">
                  처리일: {formatDate(report.reviewedAt)}
                </Typography>
                {report.reviewer && (
                  <Typography variant="caption" color="text.secondary" display="block">
                    처리자: {report.reviewer.name} ({report.reviewer.email})
                  </Typography>
                )}
              </Box>
            )}
          </Paper>
        </Box>

        {/* 신고자 정보 */}
        <Box sx={{ flex: { xs: 1, md: 1 } }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              신고자 정보
            </Typography>
            <Divider sx={{ mb: 2 }} />

            <Typography variant="subtitle1">{report.reporter.name}</Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              @{report.reporter.username}
            </Typography>

            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>이메일:</strong> {report.reporter.email}
            </Typography>

            <Typography variant="caption" color="text.secondary">
              가입일: {formatDate(report.reporter.createdAt)}
            </Typography>
          </Paper>
        </Box>
      </Box>

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} fullScreen={isMobile}>
        <DialogTitle>신고 삭제 확인</DialogTitle>
        <DialogContent>
          이 신고를 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>취소</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            삭제
          </Button>
        </DialogActions>
      </Dialog>

      {/* 게시물 즉시 조치 다이얼로그 (숨김 / 삭제) */}
      <Dialog
        open={postActionOpen !== null}
        onClose={() => !isPostActionBusy && setPostActionOpen(null)}
        fullScreen={isMobile}
      >
        <DialogTitle>
          {postActionOpen === 'hide'
            ? '게시물 숨김 처리'
            : postActionOpen === 'unhide'
            ? '숨김 해제'
            : '게시물 즉시 삭제'}
        </DialogTitle>
        <DialogContent>
          {postActionOpen === 'hide' ? (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                피드·검색·유저 프로필·일지에서 즉시 제외되며 작성자 본인에게도 노출되지 않습니다.
                레코드는 유지되므로 필요 시 복구할 수 있습니다. 이 신고는 자동 RESOLVED 로 처리됩니다.
              </Typography>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="숨김 사유 (선택, 관리자 로그에 기록)"
                value={postActionReason}
                onChange={(e) => setPostActionReason(e.target.value)}
                autoFocus
              />
            </>
          ) : postActionOpen === 'unhide' ? (
            <Typography variant="body2">
              게시물을 다시 공개 상태(PUBLIC)로 되돌립니다. 피드·검색·유저 프로필·일지에서
              바로 보이기 시작하며 작성자 본인에게도 다시 노출됩니다. 숨김 사유/처리자 기록은 초기화됩니다.
            </Typography>
          ) : (
            <Typography variant="body2" color="error">
              게시물과 관련된 이미지·좋아요·댓글·신고 이력이 모두 영구 삭제됩니다.
              이 작업은 되돌릴 수 없습니다. 숨김 처리로 충분하다면 취소하고 숨김 버튼을 이용하세요.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setPostActionOpen(null)}
            disabled={isPostActionBusy}
          >
            취소
          </Button>
          <Button
            onClick={handlePostAction}
            color={
              postActionOpen === 'hide'
                ? 'warning'
                : postActionOpen === 'unhide'
                ? 'success'
                : 'error'
            }
            variant="contained"
            disabled={isPostActionBusy}
          >
            {isPostActionBusy
              ? '처리 중...'
              : postActionOpen === 'hide'
              ? '숨김 처리'
              : postActionOpen === 'unhide'
              ? '숨김 해제'
              : '완전 삭제'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ReportDetail;
